import {
  Box,
  CircularProgress,
  LinearProgress,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

export type WizardStepState = 'done' | 'active' | 'running' | 'todo';

export interface WizardStep {
  key: string;
  label: string;
  /** Defaults to done / active / todo by position relative to `activeStep`. */
  state?: WizardStepState;
  /** Renders the step as a button that reports its key to `onStepClick`. */
  clickable?: boolean;
}

interface WizardStepperProps {
  steps: WizardStep[];
  activeStep: number;
  onStepClick?: (key: string) => void;
}

/** "You are here": marks the step whose content the page shows, whatever its journey state. */
const viewedStepSx = {
  '&& .MuiStepLabel-label': { color: 'primary.main', fontWeight: 700 },
  '& .MuiStepLabel-iconContainer': { borderRadius: '50%', boxShadow: '0 0 0 3px var(--color-primary-line)' },
};

/**
 * The progress line shared by the onboarding wizard and the validation
 * page, so the journey reads as one sequence of steps across both. Each
 * step draws its journey state — done, active (the journey's current
 * step), running (a spinner in place of its number) or todo — and the
 * step at `activeStep`, the one whose content the page shows, is ringed
 * and labelled in primary, so "where the journey is" and "what I am
 * looking at" both read at a glance. Below the `md` breakpoint it
 * collapses to "Step n of m · label" plus a progress bar.
 */
export function WizardStepper({ steps, activeStep, onStepClick }: WizardStepperProps) {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const current = steps[activeStep];

  if (compact) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Step {activeStep + 1} of {steps.length}
          {current ? ` · ${current.label}` : ''}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={steps.length > 0 ? ((activeStep + 1) / steps.length) * 100 : 0}
          aria-hidden
          sx={{ mt: 0.75, borderRadius: 'var(--radius-full)' }}
        />
      </Box>
    );
  }

  return (
    <Stepper
      nonLinear
      alternativeLabel
      activeStep={activeStep}
      sx={{ '& .MuiStepLabel-label': { fontSize: 12, lineHeight: 1.3, mt: 0.75 } }}
    >
      {steps.map((step, i) => {
        const state = step.state ?? (i < activeStep ? 'done' : i === activeStep ? 'active' : 'todo');
        const icon = state === 'running' ? <CircularProgress size={22} /> : undefined;
        const clickable = Boolean(step.clickable && onStepClick);
        return (
          <Step
            key={step.key}
            completed={state === 'done'}
            active={state === 'active' || state === 'running'}
            sx={i === activeStep ? viewedStepSx : undefined}
          >
            {clickable ? (
              <StepButton icon={icon} onClick={() => onStepClick?.(step.key)}>
                {step.label}
              </StepButton>
            ) : (
              <StepLabel icon={icon}>{step.label}</StepLabel>
            )}
          </Step>
        );
      })}
    </Stepper>
  );
}
